import { API, type ApiService, LOGGER, type Logger } from "@packages/common";
import { Inject, Injectable } from "@webmotors/ioc";
import type { FetchCurrentLocationDTO } from "./dtos";
import type { CurrentLocationApiResponseDTO } from "./dtos/current-location-api-response.dto";
import {
  type CurrentLocation,
  type CurrentLocationCoords,
  CurrentLocationMode,
} from "./models";

const ENABLED_STATES = ["sp", "pe", "ce", "mg"];

@Injectable("CurrentLocationService")
export class CurrentLocationService {
  private readonly logger: Logger;

  /**
   * Location service constructor
   *
   * @param api
   */
  constructor(
    @Inject(API) private readonly api: ApiService,
    @Inject(LOGGER)
    private readonly loggerFactory: Logger
  ) {
    this.logger = this.loggerFactory.create("CurrentLocationService");
  }

  /**
   * Get user current coordinates
   *
   * @returns {Promise<GeolocationPosition>}
   */
  getCurrentCoords(): Promise<CurrentLocationCoords | null> {
    return new Promise((resolve) => {
      this.logger.debug("[getCurrentCoords] getting current coords...");

      let resolved = false;

      if (typeof navigator === "undefined") {
        this.logger.debug("[getCurrentCoords] navigator is not defined");

        return resolve(null);
      }

      setTimeout(() => {
        if (!resolved) {
          resolved = true;

          this.logger.debug("[getCurrentCoords] resolved due timeout");

          resolve(null);
        }
      }, 2000);

      return navigator.geolocation.getCurrentPosition(
        (coords) => {
          if (!resolved) {
            resolved = true;

            this.logger.debug("[getCurrentCoords] coords were fetched: ", {
              latitude: coords.coords.latitude,
              longitude: coords.coords.longitude,
            });

            resolve({
              latitude: coords.coords.latitude,
              longitude: coords.coords.longitude,
            });
          }
        },
        () => {
          if (!resolved) {
            resolved = true;

            this.logger.debug("[getCurrentCoords] failed to get coords");

            resolve(null);
          }
        },
        {
          timeout: 1000,
        }
      );
    });
  }

  /**
   * Get current user location
   *
   * @returns
   */
  async getCurrentLocation({
    isMobile,
  }: {
    isMobile: boolean;
  }): Promise<CurrentLocation> {
    const coords = await this.getCurrentCoords();

    return this.fetch({
      ...coords,
      isMobile,
    });
  }

  /**
   * Fetch user current location
   *
   * @returns
   */
  async fetch(params: FetchCurrentLocationDTO): Promise<CurrentLocation> {
    this.logger.debug("[fetch] fetching current location...");

    const mode =
      params.latitude && params.longitude
        ? CurrentLocationMode.COORDS
        : CurrentLocationMode.IP;

    this.logger.debug("[fetch] using mode: ", mode);

    if (mode === CurrentLocationMode.IP && params.isMobile) {
      this.logger.debug("[fetch] IP mode is disabled for mobile application");

      return {
        city: null,
        abbr: null,
        mode: CurrentLocationMode.NONE,
      };
    }

    try {
      this.logger.debug("[fetch] Fetching location on api");

      const data = await this.api.get<
        {
          lat?: string | null | number;
          long?: string | null | number;
        },
        CurrentLocationApiResponseDTO
      >("/location", {
        lat: params.latitude,
        long: params.longitude,
      });

      const abbr = data.response?.["edge-region"];

      this.logger.debug("[fetch] got state: ", abbr);

      if (
        mode === CurrentLocationMode.IP &&
        !ENABLED_STATES.includes(String(abbr))
      ) {
        this.logger.debug("[fetch] state is disabled for IP mode");

        return {
          city: null,
          abbr: null,
          mode: CurrentLocationMode.NONE,
        };
      }

      this.logger.debug("[fetch] result was", {
        city: data.response?.["edge-city"],
        abbr,
        mode,
      });

      return {
        city: data.response?.["edge-city"],
        abbr,
        mode,
      };
    } catch {
      this.logger.debug("[fetch] api request failed");

      return {
        city: null,
        abbr: null,
        mode: CurrentLocationMode.NONE,
      };
    }
  }
}
